import { useCallback, useEffect, useRef, useState } from 'react';
import type { PhotoDetail } from '@/types/photo';
import { decodeSlideImage, slideImageSource, SLIDE_EFFECTS, type SlideEffect } from './slideMedia';
import './slideshow.css';

type Scene = { photo: PhotoDetail; src: string; effect: SlideEffect; direction: number };
type Props = { photo: PhotoDetail; playing: boolean; muted: boolean; direction: number; sequence: number; onReady: (ready: boolean) => void; onEnded: () => void };

export default function SlideStage({photo, playing, muted, direction, sequence, onReady, onEnded}: Props) {
  const [scene, setScene] = useState<Scene | null>(null);
  const [previous, setPrevious] = useState<Scene | null>(null);
  const [readyId, setReadyId] = useState('');
  const [error, setError] = useState('');
  const [needsTap, setNeedsTap] = useState(false);
  const [retry, setRetry] = useState(0);
  const sceneRef = useRef<Scene | null>(null);
  const requestedId = useRef(photo.id);
  requestedId.current = photo.id;
  const videos = useRef(new Map<string, HTMLVideoElement>());

  useEffect(() => {
    let cancelled = false;
    onReady(false); setError(''); setNeedsTap(false);
    const base = slideImageSource(photo);
    const src = retry ? `${base}${base.includes('?') ? '&' : '?'}retry=${retry}` : base;
    void decodeSlideImage(src).then(() => {
      if (cancelled) return;
      const originalVideo = photo.originalUrl || photo.image;
      const videoSource = /\/api\/photos\/[^/]+\/playback/.test(originalVideo)
        ? `${originalVideo}${originalVideo.includes('?') ? '&' : '?'}slideshowMedia=2` : originalVideo;
      const next: Scene = {photo, src: photo.mediaType === 'video' ? (retry ? `${videoSource}${videoSource.includes('?') ? '&' : '?'}retry=${retry}` : videoSource) : src, effect: SLIDE_EFFECTS[sequence % SLIDE_EFFECTS.length], direction};
      const old = sceneRef.current;
      setPrevious(old && old.photo.id !== photo.id ? old : null);
      sceneRef.current = next; setReadyId(''); setScene(next);
    }).catch(() => {
      if (!cancelled) setError('사진을 불러오지 못했습니다. 다시 시도하거나 다음 사진으로 넘겨 주세요.');
    });
    return () => { cancelled = true; };
  }, [photo, direction, sequence, retry, onReady]);

  const ready = useCallback((id: string) => {
    if (id !== requestedId.current || id !== sceneRef.current?.photo.id) return;
    setReadyId(id); setError(''); onReady(true);
  }, [onReady]);

  useEffect(() => {
    if (!scene || readyId !== scene.photo.id || !previous) return;
    const timer = window.setTimeout(() => setPrevious(null), 640);
    return () => window.clearTimeout(timer);
  }, [scene, readyId, previous]);

  useEffect(() => {
    for (const [id, video] of videos.current) {
      if (id !== scene?.photo.id || !playing) { video.pause(); continue; }
      video.muted = muted;
      void video.play().then(() => setNeedsTap(false)).catch((reason: unknown) => {
        if (requestedId.current !== id) return;
        if (reason instanceof DOMException && reason.name === 'AbortError') return;
        setNeedsTap(true);
      });
    }
  }, [scene, playing, muted]);

  const reveal = !!scene && readyId === scene.photo.id;
  const layers = [previous, scene].filter((value): value is Scene => value !== null);
  return <div className="slideshow-stage" data-effect={scene?.effect || 'dissolve'} style={{'--slide-direction': scene?.direction || 1} as React.CSSProperties}>
    {layers.map((layer) => {
      const current = layer.photo.id === scene?.photo.id;
      return <div key={layer.photo.id} aria-hidden={!current} className={`slideshow-layer ${current ? (reveal ? 'slide-enter' : 'slide-pending') : (reveal ? 'slide-exit' : 'slide-hold')}`}>
        {layer.photo.mediaType === 'video' ? <video
          ref={(element) => { if (element) videos.current.set(layer.photo.id, element); else videos.current.delete(layer.photo.id); }}
          src={layer.src} poster={layer.photo.image} crossOrigin="use-credentials" preload="auto"
          muted={!current || muted} playsInline autoPlay={current && playing}
          onLoadedData={() => { if (current) ready(layer.photo.id); }}
          onCanPlay={() => { if (current) ready(layer.photo.id); }}
          onEnded={() => { if (current && requestedId.current === layer.photo.id) onEnded(); }}
          onError={() => { if (current) setError('동영상을 재생하지 못했습니다. 다시 시도하거나 다음으로 넘겨 주세요.'); }}
          className="slideshow-media" /> : <img
          src={layer.src} decoding="async" draggable={false}
          alt={current ? (layer.photo.caption || '가족 사진') : ''}
          onLoad={() => { if (current) ready(layer.photo.id); }}
          onError={() => { if (current) setError('사진을 표시하지 못했습니다. 다시 시도해 주세요.'); }}
          className="slideshow-media" />}
      </div>;
    })}
    {(error || needsTap) && <div className="slideshow-media-notice" role={error ? 'alert' : 'status'}>
      <p>{error || '화면을 눌러 동영상을 재생해 주세요.'}</p>
      <button type="button" onClick={(event) => {
        event.stopPropagation();
        if (error) { videos.current.get(sceneRef.current?.photo.id || '')?.load(); setError(''); setRetry((value) => value + 1); return; }
        const video = videos.current.get(sceneRef.current?.photo.id || '');
        if (video) void video.play().then(() => setNeedsTap(false)).catch(() => setError('동영상을 재생하지 못했습니다. 다음으로 넘겨 주세요.'));
      }}>{error ? '다시 시도' : '동영상 재생'}</button>
    </div>}
  </div>;
}
